import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, Stack } from '@mui/material';
import { useCreateDatasetMutation, useEditDatasetMutation } from '~/api/v2/hooks';
import { ButtonSpinner } from '~/components';
import { modalActionBlockStyles } from '~/components/styles';
import { useAppDispatch, useAppSelector } from '~/redux';
import { ModalTypesEnum, closeModalById } from '~/redux/slices/modalSlice';
import { DatasetCreationStepsEnum, initialDefaults } from './types';

interface ModalActionBlockProps {
  activeStep: DatasetCreationStepsEnum;
  setActiveStep: Dispatch<SetStateAction<DatasetCreationStepsEnum>>;
}

const ModalActionBlock: FC<ModalActionBlockProps> = ({ activeStep, setActiveStep }) => {
  const datasetId = +useParams().id!;
  const dispatch = useAppDispatch();
  const modals = useAppSelector((state) => state.modal);
  const datasetMutation = useCreateDatasetMutation({
    fixedCacheKey: `create-dataset`,
  });
  const editDatasetMutation = useEditDatasetMutation({
    fixedCacheKey: `edit-dataset`,
  });
  const { isLoading } = datasetId ? editDatasetMutation[1] : datasetMutation[1];

  const {
    formState: { isValid, errors },
    reset,
    watch,
  } = useFormContext();

  const stepsCount = Object.values(DatasetCreationStepsEnum).filter((v) => typeof v === 'number').length;
  const isLastStep = useMemo(() => activeStep === stepsCount - 1, [activeStep, stepsCount]);
  const modalType = datasetId ? ModalTypesEnum.EDIT_DATASET : ModalTypesEnum.CREATE_DATASET;
  const modalId = modals.find((modal) => modal.type === modalType)?.id;

  const handleClose = () => {
    if (isLoading) return;

    if (modalId) {
      dispatch(closeModalById(modalId));
    }

    if (!datasetId) reset(initialDefaults);
    setActiveStep(DatasetCreationStepsEnum.DETAILS);
  };

  const cleanValidationErrors = () => {
    reset(watch(), { keepValues: true, keepErrors: false, keepDirty: true });
  };

  const handleNext = () => {
    if (isLastStep) return;

    cleanValidationErrors();
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    cleanValidationErrors();
    setActiveStep(activeStep - 1);
  };

  return (
    <Box sx={modalActionBlockStyles}>
      <CustomButton buttonType="borderless" label="Cancel" onClick={handleClose} />
      <Stack direction="row" spacing={2}>
        {activeStep !== DatasetCreationStepsEnum.DETAILS && (
          <CustomButton buttonType="outlined" label="Back" onClick={handleBack} />
        )}
        <CustomButton
          type="submit"
          disabled={!!Object.keys(errors).length || !isValid || isLoading}
          onClick={handleNext}
        >
          {isLastStep ? (datasetId ? 'Save' : 'Create') : 'Next'}
          {isLoading && <ButtonSpinner />}
        </CustomButton>
      </Stack>
    </Box>
  );
};

export default ModalActionBlock;
